<template>
  <div>
    <global-confirm-box ref="confirmBox" />
    <warehouse-dialog
      v-model="warehouseDialog"
      :edited-item="editedItem"
      @create="onWarehouseCreated"
      @update="onWarehouseUpdated"
    />

    <!-- Mobile View -->
    <template v-if="device.isMobile">
      <mobile-data-cards
        :headers="mobileHeaders"
        :items="customSearch(translatedWarehouses, headers, filters.search)"
        item-key="id"
        show-filters
        :fields-per-row="3"
        :show-row-number="false"
        :bold-fields="['name']"
      >
        <template v-slot:item.actions="{ item }">
          <v-spacer />
          <edit-button class="mr-2" @click="editItem(item)" />
          <remove-button @click="deleteItem(item)" />
        </template>
      </mobile-data-cards>

      <v-btn fab fixed bottom right color="primary" @click="warehouseDialog = true">
        <v-icon>mdi-plus</v-icon>
      </v-btn>
    </template>

    <!-- Desktop View -->
    <template v-else>
      <v-data-table
        :headers="headers"
        :items="customSearch(translatedWarehouses, headers, filters.search)"
        class="striped-table ordera-box-shadow"
      >
        <template v-slot:top>
          <v-toolbar flat>
            <v-spacer />
            <primary-button @click="warehouseDialog = true">
              <v-icon class="mr-2">mdi-plus-circle</v-icon>
              {{ $t("new_warehouse") }}
            </primary-button>
          </v-toolbar>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-row no-gutters justify="end">
            <edit-button class="mr-2" @click="editItem(item)" />
            <remove-button @click="deleteItem(item)" />
          </v-row>
        </template>
      </v-data-table>
    </template>
  </div>
</template>

<script>
import WarehouseDialog from "@/components/warehouse/dialogs/WarehouseDialog.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import { warehouseItem } from "@/models/warehouse";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";
import hasSnackbar from "@/mixins/hasSnackbar";
import warehouseService from "@/services/warehouse";

export default {
  mixins: [hasPrinting, hasExcel, hasSnackbar],
  components: {
    WarehouseDialog,
    MobileDataCards,
  },
  data: () => ({
    warehouses: [],
    warehouseDialog: false,
    editedItem: { ...warehouseItem },
  }),
  created() {
    this.initialize();
  },
  methods: {
    async initialize() {
      try {
        const res = await warehouseService.getWarehouses();
        this.warehouses = res.data;
      } catch (e) {
        this.showErrorSnackbar(this.$t("error_loading_warehouses"));
      }
    },

    editItem(item) {
      this.editedItem = Object.assign({}, item);
      this.warehouseDialog = true;
    },

    deleteItem(item) {
      this.$refs.confirmBox
        .open(
          this.$t("warehouse.delete_issue"),
          this.$t("warehouse.confirm_delete_warehouse", { name: item.name }),
          {
            color: "warning",
          }
        )
        .then(() => {
          warehouseService
            .removeWarehouse(item.id)
            .then(() => {
              this.warehouses = this.warehouses.filter((i) => i.id !== item.id);
              this.showSuccessSnackbar(this.$t("warehouse_deleted"));
            })
            .catch((err) => {
              console.warn(err);
              this.showErrorSnackbar(this.$t("error_deleting_warehouse"));
            });
        })
        .catch((err) => console.warn(err));
    },

    onWarehouseCreated(newWarehouse) {
      this.warehouses.push(newWarehouse);
      this.showSuccessSnackbar(this.$t("warehouse_created"));
    },
    onWarehouseUpdated(updatedWarehouse) {
      const index = this.warehouses.findIndex((item) => item.id === updatedWarehouse.id);
      if (index !== -1) {
        this.$set(this.warehouses, index, updatedWarehouse);
        this.showSuccessSnackbar(this.$t("warehouse_updated"));
      }
    },
  },
  computed: {
    headers() {
      return [
        {
          text: this.$t("title"),
          align: "start",
          sortable: false,
          value: "name",
          cols: 6,
        },
        { text: this.$t("address"), value: "location", cols: 3 },
        { text: this.$t("note"), value: "notes", cols: 3, mobile: false },
        {
          text: this.$t("actions"),
          value: "actions",
          sortable: false,
          print: false,
          cols: 3,
        },
      ];
    },
    mobileHeaders() {
      return this.headers.filter((h) => h.mobile !== false);
    },
    translatedWarehouses() {
      return this.warehouses.map((item) => {
        return {
          ...item,
          name: this.toLatin(item.name),
          location: this.translate(item.location),
          notes: this.translate(item.notes),
        };
      });
    },
    filters() {
      return this.$store.state.warehouse.filters;
    },
    exportItems() {
      return this.customSearch(
        this.translatedWarehouses,
        this.headers,
        this.filters.search
      );
    },
    exportTitle() {
      return this.$t("warehouses");
    },
  },
  watch: {
    "$store.state.warehouse.refreshTrigger"() {
      this.initialize();
    },
    warehouseDialog(val) {
      if (!val) {
        this.editedItem = warehouseItem;
      }
    },
    isPrinting(isPrinting) {
      if (isPrinting) {
        this.printDocument({
          title: this.exportTitle,
          subtitle: this.subtitle,
          tables: [
            {
              headers: this.headers,
              items: this.exportItems,
            },
          ],
        });
      }
    },
    isExcelDownloading() {
      if (this.isExcelDownloading) {
        this.downloadExcel({
          fileName: this.exportTitle + ".xlsx",
          sheets: [
            {
              name: this.exportTitle,
              headers: this.headers,
              items: this.exportItems,
              title: this.exportTitle,
              subtitle: this.subtitle,
            },
          ],
        });
      }
    },
  },
};
</script>
